import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, useAccountWallet, useSimBalance, useTokenData } from "../../../global-account/react/index.js";
import { cn } from "../../../shared/utils/cn.js";
import { NetworkArbitrumOne, NetworkBase, NetworkBinanceSmartChain, NetworkEthereum, NetworkOptimism, NetworkPolygonPos } from "@web3icons/react";
import { ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AnySpend } from "./AnySpend.js";
import { AnySpendCustomExactIn } from "./AnySpendCustomExactIn.js";
import { ChainWarningText } from "./common/WarningText.js";
import { CreditCardIcon } from "./icons/CreditCardIcon.js";
import { QrCodeIcon } from "./icons/QrCodeIcon.js";
import { QRDeposit } from "./QRDeposit.js";
// Default supported chains
const DEFAULT_SUPPORTED_CHAINS = [{
  id: 8453,
  name: "Base"
}, {
  id: 1,
  name: "Ethereum"
}, {
  id: 42161,
  name: "Arbitrum"
}, {
  id: 10,
  name: "Optimism"
}, {
  id: 137,
  name: "Polygon"
}, {
  id: 56,
  name: "BNB Chain"
}];
// Minimum pool size to filter out low liquidity tokens
const DEFAULT_MIN_POOL_SIZE = 1000000;
function formatUsd(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: value >= 100 ? 0 : 2
  }).format(value);
}
function formatDecimal(value) {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: value >= 100 ? 0 : 2
  }).format(value);
}
// Chain icon component
function ChainIcon({
  chainId,
  className
}) {
  const iconProps = {
    variant: "branded",
    className: cn("anyspend-deposit-chain-icon", className)
  };
  switch (chainId) {
    case 1:
      return _jsx(NetworkEthereum, {
        ...iconProps
      });
    case 8453:
      return _jsx(NetworkBase, {
        ...iconProps
      });
    case 137:
      return _jsx(NetworkPolygonPos, {
        ...iconProps
      });
    case 42161:
      return _jsx(NetworkArbitrumOne, {
        ...iconProps
      });
    case 10:
      return _jsx(NetworkOptimism, {
        ...iconProps
      });
    case 56:
      return _jsx(NetworkBinanceSmartChain, {
        ...iconProps
      });
    default:
      return null;
  }
}
/**
 * A flexible deposit component that wraps AnySpendCustomExactIn with optional chain selection.
 *
 * @example
 * // Simple deposit with chain selection
 * <AnySpendDeposit
 *   recipientAddress={userAddress}
 *   destinationTokenAddress="0x..."
 *   destinationTokenChainId={base.id}
 *   onSuccess={(amount) => console.log(`Deposited ${amount}`)}
 * />
 *
 * @example
 * // Skip chain selection by providing sourceTokenChainId
 * <AnySpendDeposit
 *   recipientAddress={userAddress}
 *   destinationTokenAddress="0x..."
 *   destinationTokenChainId={base.id}
 *   sourceTokenChainId={base.id}
 *   onSuccess={(amount) => console.log(`Deposited ${amount}`)}
 * />
 *
 * @example
 * // Deposit with custom contract
 * <AnySpendDeposit
 *   recipientAddress={userAddress}
 *   destinationTokenAddress="0x..."
 *   destinationTokenChainId={base.id}
 *   depositContractConfig={{
 *     contractAddress: "0x...",
 *     functionName: "depositFor",
 *   }}
 *   onSuccess={(amount) => console.log(`Deposited ${amount}`)}
 * />
 */
export function AnySpendDeposit({
  loadOrder,
  mode = "modal",
  recipientAddress,
  paymentType: initialPaymentType,
  sourceTokenAddress,
  sourceTokenChainId: initialSourceChainId,
  destinationTokenAddress,
  destinationTokenChainId,
  onSuccess,
  onOpenCustomModal,
  mainFooter,
  onTokenSelect,
  customUsdInputValues,
  preferEoa,
  minDestinationAmount,
  header,
  orderType,
  depositContractConfig,
  showChainSelection,
  showFiatOption = true,
  supportedChains = DEFAULT_SUPPORTED_CHAINS,
  minPoolSize = DEFAULT_MIN_POOL_SIZE,
  topChainsCount = 3,
  onClose,
  returnToHomeUrl,
  customRecipientLabel,
  returnHomeLabel,
  isCustomDeposit = false,
  classes,
  allowDirectTransfer = false,
  destinationTokenAmount,
  callbackMetadata,
  senderAddress,
  slots,
  content,
  theme
}) {
  // Extract deposit-specific classes for convenience
  const depositClasses = classes?.deposit;
  const {
    connectedEOAWallet
  } = useAccountWallet();
  const eoaAddress = connectedEOAWallet?.getAccount()?.address;
  const effectiveInitialPaymentType = !showFiatOption && initialPaymentType === "fiat" ? "crypto" : initialPaymentType;
  // Determine if we should show chain selection
  const shouldShowChainSelection = showChainSelection ?? (!initialSourceChainId && !effectiveInitialPaymentType);
  const [step, setStep] = useState(shouldShowChainSelection ? "select-chain" : "deposit");
  const [selectedChainId, setSelectedChainId] = useState(initialSourceChainId);
  const [paymentType, setPaymentType] = useState(effectiveInitialPaymentType ?? "crypto");
  useEffect(() => {
    if (!showFiatOption && paymentType === "fiat") {
      setPaymentType("crypto");
    }
  }, [showFiatOption, paymentType]);
  // Fetch destination token data
  const {
    data: destinationTokenData
  } = useTokenData(destinationTokenChainId, destinationTokenAddress);
  // Construct full destination token object
  const destinationToken = useMemo(() => ({
    address: destinationTokenAddress,
    chainId: destinationTokenChainId,
    symbol: destinationTokenData?.symbol ?? "",
    name: destinationTokenData?.name ?? "",
    decimals: destinationTokenData?.decimals ?? 18,
    metadata: {
      logoURI: destinationTokenData?.logoURI
    }
  }), [destinationTokenAddress, destinationTokenChainId, destinationTokenData]);
  // Fetch balances for EOA wallet (use senderAddress as fallback for pre-filled balance display)
  const effectiveBalanceAddress = senderAddress || eoaAddress;
  const {
    data: balanceData,
    isLoading: isBalanceLoading
  } = useSimBalance(shouldShowChainSelection ? effectiveBalanceAddress : undefined, supportedChains.map(c => c.id));
  // Group balances by chain and calculate total USD value per chain
  const chainBalances = useMemo(() => {
    if (!balanceData?.balances) return {};
    const filteredBalances = balanceData.balances.filter(token => token.address === "native" || token.pool_size && token.pool_size > minPoolSize);
    return filteredBalances.reduce((acc, token_0) => {
      const chainId = token_0.chain_id;
      if (!acc[chainId]) {
        acc[chainId] = {
          chainId,
          chainName: token_0.chain,
          totalUsdValue: 0,
          tokenCount: 0
        };
      }
      acc[chainId].totalUsdValue += token_0.value_usd || 0;
      acc[chainId].tokenCount += 1;
      return acc;
    }, {});
  }, [balanceData, minPoolSize]);
  // Sort chains by USD value (highest first)
  const sortedChains = useMemo(() => {
    return supportedChains.map(chain => ({
      ...chain,
      balance: chainBalances[chain.id]?.totalUsdValue || 0,
      tokenCount: chainBalances[chain.id]?.tokenCount || 0
    })).sort((a, b) => b.balance - a.balance);
  }, [supportedChains, chainBalances]);
  // Get top chains with balance
  const topChainsWithBalance = useMemo(() => {
    return sortedChains.filter(chain_0 => chain_0.balance > 0).slice(0, topChainsCount);
  }, [sortedChains, topChainsCount]);
  // Calculate total balance across all chains
  const totalBalance = useMemo(() => {
    return Object.values(chainBalances).reduce((sum, chain_1) => sum + chain_1.totalUsdValue, 0);
  }, [chainBalances]);
  if (!recipientAddress) return null;
  const tokenSymbol = destinationToken.symbol || "TOKEN";
  // Determine order type based on config
  const effectiveOrderType = orderType ?? (depositContractConfig ? "custom_exact_in" : "swap");
  // Default header if not provided
  const defaultHeader = () => _jsx("div", {
    className: "anyspend-deposit-header mb-4 flex flex-col items-center gap-3 text-center",
    children: _jsx("div", {
      children: _jsx("h1", {
        className: "anyspend-deposit-title text-as-primary text-xl font-bold",
        children: paymentType === "crypto" ? `Deposit ${tokenSymbol}` : "Fund with Fiat"
      })
    })
  });
  const handleSelectChain = chainId_0 => {
    setSelectedChainId(chainId_0);
    setPaymentType("crypto");
    setStep("deposit");
  };
  const handleSelectCrypto = () => {
    setPaymentType("crypto");
    setSelectedChainId(undefined);
    setStep("deposit");
  };
  const handleSelectFiat = () => {
    setPaymentType("fiat");
    setSelectedChainId(undefined);
    setStep("deposit");
  };
  const handleBack = () => {
    setStep("select-chain");
    setSelectedChainId(undefined);
  };
  const handleSelectQrDeposit = () => {
    setStep("qr-deposit");
  };
  // Chain selection view
  if (step === "select-chain") {
    return _jsxs("div", {
      className: depositClasses?.chainSelection || cn("anyspend-deposit anyspend-deposit-chain-selection font-inter bg-as-surface-primary relative mx-auto w-full max-w-[460px]", mode === "page" && "border-as-border-secondary overflow-hidden rounded-2xl border shadow-xl"),
      children: [onClose && _jsx("button", {
        onClick: onClose,
        className: depositClasses?.closeButton || "anyspend-deposit-close-button text-as-secondary hover:text-as-primary absolute right-4 top-4 z-10",
        children: _jsx("svg", {
          className: "h-6 w-6",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor",
          children: _jsx("path", {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: 2,
            d: "M6 18L18 6M6 6l12 12"
          })
        })
      }), !isBalanceLoading && totalBalance > 0 && _jsxs("div", {
        className: depositClasses?.balanceContainer || "anyspend-deposit-balance border-theme-border-secondary border-b p-5",
        children: [_jsx("p", {
          className: depositClasses?.balanceLabel || "anyspend-deposit-balance-label text-as-secondary text-sm",
          children: "Your Balance"
        }), _jsxs("p", {
          className: depositClasses?.balanceValue || "anyspend-deposit-balance-value text-as-primary text-3xl font-semibold",
          children: [formatDecimal(totalBalance), " ", _jsx("span", {
            className: "text-sm",
            children: "USD"
          })]
        })]
      }), _jsxs("div", {
        className: depositClasses?.optionsContainer || "anyspend-deposit-options flex flex-col gap-2 p-6",
        children: [isBalanceLoading && _jsx("div", {
          className: depositClasses?.chainsSkeleton || "anyspend-deposit-chains-skeleton flex flex-col gap-2",
          children: [1, 2].map((_, i) => _jsxs("div", {
            className: depositClasses?.skeletonItem || "border-border-primary flex items-center justify-between rounded-xl border p-4",
            children: [_jsxs("div", {
              className: "flex items-center gap-3",
              children: [_jsx(Skeleton, {
                className: "h-6 w-6 rounded-full"
              }), _jsxs("div", {
                className: "flex flex-col gap-1",
                children: [_jsx(Skeleton, {
                  className: "h-4 w-32"
                }), _jsx(Skeleton, {
                  className: "h-3 w-20"
                })]
              })]
            }), _jsx(Skeleton, {
              className: "h-5 w-5"
            })]
          }, i))
        }), topChainsWithBalance.length > 0 && _jsx("div", {
          className: depositClasses?.chainsContainer || "anyspend-deposit-chains flex flex-col gap-2",
          children: topChainsWithBalance.map(chain_2 => _jsxs("button", {
            onClick: () => handleSelectChain(chain_2.id),
            className: depositClasses?.chainButton || "anyspend-deposit-chain-button border-border-primary hover:border-as-brand hover:bg-as-surface-secondary flex w-full items-center justify-between rounded-xl border p-4 text-left shadow-sm transition-all",
            children: [_jsx("div", {
              className: depositClasses?.chainContent || "anyspend-deposit-chain-content",
              children: _jsxs("div", {
                className: depositClasses?.chainInfo || "anyspend-deposit-chain-info",
                children: [_jsxs("span", {
                  className: depositClasses?.chainName || "anyspend-deposit-chain-name text-as-primary flex items-center gap-1.5 font-medium",
                  children: ["Deposit from ", chain_2.name, _jsx(ChainIcon, {
                    chainId: chain_2.id,
                    className: depositClasses?.chainIcon || "h-5 w-5"
                  })]
                }), _jsxs("p", {
                  className: depositClasses?.chainBalance || "anyspend-deposit-chain-balance text-as-secondary text-xs",
                  children: [formatUsd(chain_2.balance), " available"]
                })]
              })
            }), _jsx(ChevronRight, {
              className: depositClasses?.chainChevron || "anyspend-deposit-chain-chevron text-as-secondary h-5 w-5"
            })]
          }, chain_2.id))
        }), _jsxs("div", {
          className: depositClasses?.generalOptions || "anyspend-deposit-general-options flex flex-col gap-2",
          children: [_jsxs("button", {
            onClick: handleSelectCrypto,
            className: depositClasses?.cryptoButton || "anyspend-deposit-option-button anyspend-deposit-crypto-button border-border-primary hover:border-as-brand hover:bg-as-surface-secondary flex w-full items-center justify-between rounded-xl border p-4 text-left shadow-sm transition-all",
            children: [_jsx("div", {
              className: depositClasses?.optionContent || "anyspend-deposit-option-content",
              children: _jsxs("div", {
                className: depositClasses?.optionInfo || "anyspend-deposit-option-info",
                children: [_jsx("span", {
                  className: depositClasses?.optionTitle || "anyspend-deposit-option-title text-as-primary font-medium",
                  children: "Deposit Crypto"
                }), _jsx("p", {
                  className: depositClasses?.optionDescription || "anyspend-deposit-option-description text-as-secondary text-xs",
                  children: "Swap from any token on any chain"
                })]
              })
            }), _jsx(ChevronRight, {
              className: depositClasses?.optionChevron || "anyspend-deposit-option-chevron text-as-secondary h-5 w-5"
            })]
          }), _jsxs("div", {
            className: depositClasses?.divider || "anyspend-deposit-divider flex items-center gap-3",
            children: [_jsx("div", {
              className: depositClasses?.dividerLine || "bg-as-stroke h-px flex-1"
            }), _jsx("span", {
              className: depositClasses?.dividerText || "anyspend-deposit-divider-text text-as-secondary text-sm",
              children: "More options"
            }), _jsx("div", {
              className: depositClasses?.dividerLine || "bg-as-stroke h-px flex-1"
            })]
          }), _jsxs("button", {
            onClick: handleSelectQrDeposit,
            className: depositClasses?.qrButton || "anyspend-deposit-option-button anyspend-deposit-qr-button border-border-primary hover:border-as-brand hover:bg-as-surface-secondary flex w-full items-center justify-between rounded-xl border p-4 text-left shadow-sm transition-all",
            children: [_jsxs("div", {
              className: depositClasses?.optionContent || "anyspend-deposit-option-content flex items-center gap-3",
              children: [_jsx(QrCodeIcon, {
                className: depositClasses?.optionIcon || "anyspend-deposit-option-icon h-10 w-10"
              }), _jsxs("div", {
                className: depositClasses?.optionInfo || "anyspend-deposit-option-info",
                children: [_jsx("span", {
                  className: depositClasses?.optionTitle || "anyspend-deposit-option-title text-as-primary font-medium",
                  children: "Deposit with QR Code"
                }), _jsx("p", {
                  className: depositClasses?.optionDescription || "anyspend-deposit-option-description text-as-secondary text-xs",
                  children: "Send tokens directly to deposit address"
                })]
              })]
            }), _jsx(ChevronRight, {
              className: depositClasses?.optionChevron || "anyspend-deposit-option-chevron text-as-secondary h-5 w-5"
            })]
          }), showFiatOption && _jsxs("button", {
            onClick: handleSelectFiat,
            className: depositClasses?.fiatButton || "anyspend-deposit-option-button anyspend-deposit-fiat-button border-border-primary hover:border-as-brand hover:bg-as-surface-secondary flex w-full items-center justify-between rounded-xl border p-4 text-left transition-all",
            children: [_jsxs("div", {
              className: depositClasses?.optionContent || "anyspend-deposit-option-content flex items-center gap-3",
              children: [_jsx(CreditCardIcon, {
                className: depositClasses?.optionIcon || "anyspend-deposit-option-icon h-10 w-10"
              }), _jsxs("div", {
                className: depositClasses?.optionInfo || "anyspend-deposit-option-info",
                children: [_jsx("span", {
                  className: depositClasses?.optionTitle || "anyspend-deposit-option-title text-as-primary font-medium",
                  children: "Fund with Fiat"
                }), _jsx("p", {
                  className: depositClasses?.optionDescription || "anyspend-deposit-option-description text-as-secondary text-xs",
                  children: "Pay with card or bank transfer"
                })]
              })]
            }), _jsx(ChevronRight, {
              className: depositClasses?.optionChevron || "anyspend-deposit-option-chevron text-as-secondary h-5 w-5"
            })]
          })]
        }), _jsx(ChainWarningText, {
          chainId: destinationTokenChainId,
          classes: classes?.chainWarningText || {
            root: "mt-2"
          }
        })]
      })]
    });
  }
  // QR Deposit view
  if (step === "qr-deposit") {
    return _jsx(QRDeposit, {
      mode: mode,
      recipientAddress: recipientAddress,
      destinationToken: destinationToken,
      destinationChainId: destinationTokenChainId,
      depositContractConfig: depositContractConfig,
      onBack: handleBack,
      onClose: onClose ?? handleBack,
      classes: classes?.qrDeposit
    });
  }
  // Deposit view
  return _jsxs("div", {
    className: depositClasses?.form || "anyspend-deposit anyspend-deposit-form relative",
    children: [shouldShowChainSelection && _jsxs("button", {
      onClick: handleBack,
      className: depositClasses?.backButton || "anyspend-deposit-back-button text-as-secondary hover:text-as-primary absolute left-4 top-4 z-10 flex items-center gap-1",
      children: [_jsx("svg", {
        className: depositClasses?.backIcon || "anyspend-deposit-back-icon h-5 w-5",
        fill: "none",
        viewBox: "0 0 24 24",
        stroke: "currentColor",
        children: _jsx("path", {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: 2,
          d: "M15 19l-7-7 7-7"
        })
      }), _jsx("span", {
        className: depositClasses?.backText || "anyspend-deposit-back-text text-sm",
        children: "Back"
      })]
    }), onClose && _jsx("button", {
      onClick: onClose,
      className: depositClasses?.closeButton || "anyspend-deposit-close-button text-as-secondary hover:text-as-primary absolute right-4 top-4 z-10",
      children: _jsx("svg", {
        className: "h-6 w-6",
        fill: "none",
        viewBox: "0 0 24 24",
        stroke: "currentColor",
        children: _jsx("path", {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: 2,
          d: "M6 18L18 6M6 6l12 12"
        })
      })
    }), _jsx("div", {
      className: depositClasses?.formContent || cn("anyspend-deposit-form-content", shouldShowChainSelection && "pt-8"),
      children: isCustomDeposit ? _jsx(AnySpendCustomExactIn, {
        loadOrder: loadOrder,
        mode: mode,
        recipientAddress: recipientAddress,
        paymentType: paymentType,
        sourceTokenAddress: sourceTokenAddress,
        sourceTokenChainId: selectedChainId,
        destinationToken: destinationToken,
        destinationChainId: destinationTokenChainId,
        orderType: effectiveOrderType,
        minDestinationAmount: minDestinationAmount,
        header: header ?? defaultHeader,
        onSuccess: onSuccess,
        onOpenCustomModal: onOpenCustomModal,
        mainFooter: mainFooter,
        onTokenSelect: onTokenSelect,
        customUsdInputValues: customUsdInputValues,
        preferEoa: preferEoa,
        customExactInConfig: depositContractConfig,
        returnToHomeUrl: returnToHomeUrl,
        customRecipientLabel: customRecipientLabel,
        returnHomeLabel: returnHomeLabel,
        classes: classes?.customExactIn,
        allowDirectTransfer: allowDirectTransfer,
        destinationTokenAmount: destinationTokenAmount,
        callbackMetadata: callbackMetadata,
        senderAddress: senderAddress,
        showFiatOption: showFiatOption,
        slots: slots,
        content: content,
        theme: theme
      }, selectedChainId) : _jsx(AnySpend, {
        loadOrder: loadOrder,
        mode: mode,
        defaultActiveTab: paymentType,
        recipientAddress: recipientAddress,
        sourceChainId: selectedChainId,
        destinationTokenAddress: destinationTokenAddress,
        destinationTokenChainId: destinationTokenChainId,
        onSuccess: txHash => onSuccess?.(txHash ?? ""),
        onTokenSelect: onTokenSelect,
        customUsdInputValues: customUsdInputValues,
        hideHeader: true,
        hideBottomNavigation: true,
        disableUrlParamManagement: true,
        returnToHomeUrl: returnToHomeUrl,
        customRecipientLabel: customRecipientLabel,
        returnHomeLabel: returnHomeLabel,
        classes: classes?.anySpend,
        allowDirectTransfer: allowDirectTransfer,
        destinationTokenAmount: destinationTokenAmount,
        callbackMetadata: callbackMetadata,
        senderAddress: senderAddress,
        showFiatOption: showFiatOption,
        slots: slots,
        content: content,
        theme: theme
      }, selectedChainId)
    }), _jsx(ChainWarningText, {
      chainId: destinationTokenChainId,
      classes: classes?.chainWarningText || {
        root: "px-4 pb-4"
      }
    })]
  });
}